/**
 * Proposal Builder
 * Create and customize proposals for leads with ROI calculator
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Stack,
  Text,
  useToast,
  VStack,
  Divider,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  Checkbox,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { MdArrowBack, MdSave, MdSend, MdCalculate, MdPreview } from 'react-icons/md';
import { useAuth } from '../../../contexts/AuthContext';
import { getLead } from '../../../services/leadManagement';
import { createProposal, sendProposal } from '../../../services/proposalManagement';
import {
  Lead,
  PricingTier,
  calculatePricingTier,
  calculateEstimatedValue,
} from '../../../types/lead';
import {
  ProposalTemplate,
  ProposalPricing,
  ROICalculation,
  calculateROI,
  ProposalCreateData,
  getDefaultFeatures,
  ProposalFeature,
} from '../../../types/proposal';

export function ProposalBuilder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get('leadId');
  const toast = useToast();
  const { userProfile } = useAuth();

  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [template, setTemplate] = useState<ProposalTemplate>('standard');
  const [validityDays, setValidityDays] = useState(30);
  const [signatureRequired, setSignatureRequired] = useState(true);

  // Pricing state
  const [doorCount, setDoorCount] = useState(0);
  const [pricingTier, setPricingTier] = useState<PricingTier>('10-20');
  const [basePrice, setBasePrice] = useState(0);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [paymentSchedule, setPaymentSchedule] = useState<'annual' | 'semi-annual' | 'quarterly'>('annual');
  const [contractTerm, setContractTerm] = useState(12);

  // ROI state
  const [currentMonthlyCost, setCurrentMonthlyCost] = useState(0);
  const [timeCurrentlySpent, setTimeCurrentlySpent] = useState(40); // hours/month
  const [timeWithDossiLoc, setTimeWithDossiLoc] = useState(5); // hours/month
  const [hourlyRate, setHourlyRate] = useState(50);

  // Features state
  const [features, setFeatures] = useState<ProposalFeature[]>([]);

  // Calculated values
  const [pricing, setPricing] = useState<ProposalPricing | null>(null);
  const [roiCalculation, setRoiCalculation] = useState<ROICalculation | null>(null);

  // Pricing approval state
  const [pricingApprovalStatus, setPricingApprovalStatus] = useState<'pending' | 'approved' | 'rejected' | 'auto-approved' | null>(null);
  const [needsApproval, setNeedsApproval] = useState(false);

  // Check if custom pricing requires approval
  useEffect(() => {
    if (doorCount > 0 && basePrice > 0) {
      const standardPrice = doorCount >= 250 ? 12000 : doorCount >= 100 ? 7000 : doorCount >= 50 ? 4000 : doorCount >= 20 ? 2200 : 1200;
      const finalPrice = pricing?.finalPrice || basePrice;
      const discountPercentage = ((standardPrice - finalPrice) / standardPrice) * 100;
      setNeedsApproval(discountPercentage >= 5);
    }
  }, [doorCount, basePrice, pricing]);

  // Load lead data
  useEffect(() => {
    const loadLead = async () => {
      if (!leadId) {
        navigate('/admin/sales/leads');
        return;
      }

      try {
        setLoading(true);
        const leadData = await getLead(leadId);
        
        if (!leadData) {
          throw new Error('Lead not found');
        }
        
        setLead(leadData);

        // Pre-fill from lead data
        setDoorCount(leadData.businessInfo.doorCount);
        const tier = leadData.businessInfo.pricingTier || calculatePricingTier(leadData.businessInfo.doorCount);
        setPricingTier(tier);
        setBasePrice(leadData.estimatedValue);
        setFeatures(getDefaultFeatures(tier));
      } catch (error) {
        toast({
          title: 'Error loading lead',
          description: error instanceof Error ? error.message : 'Unknown error',
          status: 'error',
          duration: 5000,
        });
        navigate('/admin/sales/leads');
      } finally {
        setLoading(false);
      }
    };

    loadLead();
  }, [leadId]);

  // Recalculate pricing when inputs change
  useEffect(() => {
    if (!doorCount) return;

    const tier = calculatePricingTier(doorCount);
    setPricingTier(tier);

    if (!basePrice) {
      const estimated = calculateEstimatedValue(doorCount);
      setBasePrice(estimated);
    }

    const discountAmount = basePrice * (discountPercent / 100);
    const finalPrice = basePrice - discountAmount;

    const proposedMonthlyCost = finalPrice / 12;

    const calculatedPricing: ProposalPricing = {
      doorCount,
      pricingTier: tier,
      basePrice,
      discountPercent: discountPercent > 0 ? discountPercent : 0,
      discountAmount: discountAmount > 0 ? discountAmount : 0,
      finalPrice,
      paymentSchedule,
      contractTerm,
    };

    setPricing(calculatedPricing);

    // Calculate ROI
    const roi = calculateROI(
      currentMonthlyCost,
      proposedMonthlyCost,
      timeCurrentlySpent,
      timeWithDossiLoc,
      hourlyRate
    );

    setRoiCalculation(roi);
  }, [
    doorCount,
    basePrice,
    discountPercent,
    paymentSchedule,
    contractTerm,
    currentMonthlyCost,
    timeCurrentlySpent,
    timeWithDossiLoc,
    hourlyRate,
  ]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const handleFeatureToggle = (index: number) => {
    setFeatures(prev =>
      prev.map((f, i) => (i === index ? { ...f, included: !f.included } : f))
    );
  };

  const handleCreateDraft = async () => {
    if (!lead || !userProfile || !userProfile.name || !roiCalculation) {
      toast({
        title: 'Missing information',
        description: 'Please complete all required fields',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // CRITICAL FIX: Always rebuild pricing from form values to guarantee no undefined values
    // This ensures all fields are populated, even if the pricing state calculation was skipped
    const calculatedDiscountAmount = basePrice * (discountPercent / 100);
    const calculatedFinalPrice = basePrice - calculatedDiscountAmount;
    const tier = calculatePricingTier(doorCount);

    const builtPricing: ProposalPricing = {
      doorCount: doorCount || 0,
      pricingTier: tier,
      basePrice: basePrice || 0,
      discountPercent: discountPercent || 0,  // Explicitly set, never undefined
      discountAmount: calculatedDiscountAmount || 0,  // Explicitly set, never undefined
      finalPrice: calculatedFinalPrice || 0,
      paymentSchedule: paymentSchedule || 'annual',
      contractTerm: contractTerm || 12,
    };

    console.log('Frontend: Built pricing from form state (draft):', builtPricing);
    console.log('  doorCount:', builtPricing.doorCount, 'type:', typeof builtPricing.doorCount);
    console.log('  discountPercent:', builtPricing.discountPercent, 'type:', typeof builtPricing.discountPercent);
    console.log('  discountAmount:', builtPricing.discountAmount, 'type:', typeof builtPricing.discountAmount);
    console.log('  finalPrice:', builtPricing.finalPrice, 'type:', typeof builtPricing.finalPrice);

    if (!builtPricing.doorCount || !builtPricing.basePrice) {
      toast({
        title: 'Missing pricing information',
        description: 'Please fill in door count and base price',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    try {
      setSubmitting(true);

      // Validate no undefined values in pricing before sending
      Object.entries(builtPricing).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          throw new Error(`Frontend validation failed: Pricing field "${key}" is ${value}`);
        }
      });

      const proposalData: ProposalCreateData = {
        leadId: lead.id,
        template,
        pricing: builtPricing,
        roiCalculation,
        validityDays,
        signatureRequired,
      };

      console.log('Frontend: Sending proposal data (draft):', proposalData);
      console.log('User credentials for proposal:', {
        uid: userProfile.uid,
        name: userProfile.name,
        email: userProfile.email,
        role: userProfile.role
      });

      const proposal = await createProposal(
        proposalData,
        userProfile.uid,
        userProfile.name
      );

      toast({
        title: 'Proposal created',
        description: 'Draft saved successfully',
        status: 'success',
        duration: 3000,
      });

      navigate(`/admin/sales/proposals/${proposal.id}`);
    } catch (error) {
      toast({
        title: 'Error creating proposal',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateAndSend = async () => {
    if (!lead || !userProfile || !userProfile.name || !roiCalculation) {
      toast({
        title: 'Missing information',
        description: 'Please complete all required fields',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // CRITICAL FIX: Always rebuild pricing from form values to guarantee no undefined values
    // This ensures all fields are populated, even if the pricing state calculation was skipped
    const calculatedDiscountAmount = basePrice * (discountPercent / 100);
    const calculatedFinalPrice = basePrice - calculatedDiscountAmount;
    const tier = calculatePricingTier(doorCount);

    const builtPricing: ProposalPricing = {
      doorCount: doorCount || 0,
      pricingTier: tier,
      basePrice: basePrice || 0,
      discountPercent: discountPercent || 0,  // Explicitly set, never undefined
      discountAmount: calculatedDiscountAmount || 0,  // Explicitly set, never undefined
      finalPrice: calculatedFinalPrice || 0,
      paymentSchedule: paymentSchedule || 'annual',
      contractTerm: contractTerm || 12,
    };

    console.log('Frontend: Built pricing from form state (send):', builtPricing);
    console.log('  doorCount:', builtPricing.doorCount, 'type:', typeof builtPricing.doorCount);
    console.log('  discountPercent:', builtPricing.discountPercent, 'type:', typeof builtPricing.discountPercent);
    console.log('  discountAmount:', builtPricing.discountAmount, 'type:', typeof builtPricing.discountAmount);
    console.log('  finalPrice:', builtPricing.finalPrice, 'type:', typeof builtPricing.finalPrice);

    if (!builtPricing.doorCount || !builtPricing.basePrice) {
      toast({
        title: 'Missing pricing information',
        description: 'Please fill in door count and base price',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    try {
      setSubmitting(true);

      // Validate no undefined values in pricing before sending
      Object.entries(builtPricing).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          throw new Error(`Frontend validation failed: Pricing field "${key}" is ${value}`);
        }
      });

      const proposalData: ProposalCreateData = {
        leadId: lead.id,
        template,
        pricing: builtPricing,
        roiCalculation,
        validityDays,
        signatureRequired,
      };

      console.log('Frontend: Sending proposal data (send):', proposalData);
      console.log('User credentials for proposal:', {
        uid: userProfile.uid,
        name: userProfile.name,
        email: userProfile.email,
        role: userProfile.role
      });

      const proposal = await createProposal(
        proposalData,
        userProfile.uid,
        userProfile.name
      );

      await sendProposal(proposal.id, userProfile.uid, userProfile.name);

      toast({
        title: 'Proposal sent',
        description: `Proposal sent to ${lead.contactInfo.email}`,
        status: 'success',
        duration: 3000,
      });

      navigate(`/admin/sales/proposals/${proposal.id}`);
    } catch (error) {
      toast({
        title: 'Error sending proposal',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !lead) {
    return <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>Loading...</Box>;
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <HStack mb="6">
        <IconButton
          aria-label="Back"
          icon={<MdArrowBack />}
          onClick={() => navigate(`/admin/sales/leads/${leadId}`)}
        />
        <Heading size="lg">Create Proposal for {lead.contactInfo.name}</Heading>
      </HStack>

      <Grid templateColumns="repeat(12, 1fr)" gap="6">
        {/* Left Column - Form */}
        <GridItem colSpan={{ base: 12, lg: 7 }}>
          <Stack spacing="6">
            {/* Lead Summary */}
            <Card>
              <CardHeader>
                <Heading size="md">Lead Summary</Heading>
              </CardHeader>
              <CardBody>
                <VStack align="start" spacing="2">
                  <Text><strong>Name:</strong> {lead.contactInfo.name}</Text>
                  <Text><strong>Email:</strong> {lead.contactInfo.email}</Text>
                  {lead.contactInfo.company && (
                    <Text><strong>Company:</strong> {lead.contactInfo.company}</Text>
                  )}
                  <Text><strong>Door Count:</strong> {lead.businessInfo.doorCount} units</Text>
                  <Badge>{lead.businessInfo.pricingTier} tier</Badge>
                </VStack>
              </CardBody>
            </Card>

            {/* Template Selection */}
            <Card>
              <CardHeader>
                <Heading size="md">Proposal Template</Heading>
              </CardHeader>
              <CardBody>
                <FormControl>
                  <FormLabel>Template</FormLabel>
                  <Select value={template} onChange={(e) => setTemplate(e.target.value as ProposalTemplate)}>
                    <option value="standard">Standard</option>
                    <option value="premium">Premium (with case studies)</option>
                    <option value="enterprise">Enterprise (full suite)</option>
                    <option value="custom">Custom</option>
                  </Select>
                </FormControl>
              </CardBody>
            </Card>

            {/* Pricing Configuration */}
            <Card>
              <CardHeader>
                <Heading size="md">Pricing</Heading>
              </CardHeader>
              <CardBody>
                <Stack spacing="4">
                  <FormControl isRequired>
                    <FormLabel>Door Count</FormLabel>
                    <NumberInput value={doorCount} onChange={(_, val) => setDoorCount(val)} min={1}>
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <Text fontSize="sm" color="gray.500" mt="1">
                      Pricing Tier: {pricingTier}
                    </Text>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Annual Base Price</FormLabel>
                    <NumberInput value={basePrice} onChange={(_, val) => setBasePrice(val)} min={0}>
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Discount (%)</FormLabel>
                    <NumberInput
                      value={discountPercent}
                      onChange={(_, val) => setDiscountPercent(val)}
                      min={0}
                      max={50}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Payment Schedule</FormLabel>
                    <Select
                      value={paymentSchedule}
                      onChange={(e) => setPaymentSchedule(e.target.value as any)}
                    >
                      <option value="annual">Annual (full payment)</option>
                      <option value="semi-annual">Semi-Annual (2 payments)</option>
                      <option value="quarterly">Quarterly (4 payments)</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Contract Term (months)</FormLabel>
                    <Select
                      value={contractTerm}
                      onChange={(e) => setContractTerm(parseInt(e.target.value))}
                    >
                      <option value="12">12 months</option>
                      <option value="24">24 months</option>
                      <option value="36">36 months</option>
                    </Select>
                  </FormControl>

                  {pricing && (
                    <Box p="4" bg="blue.50" borderRadius="md">
                      <VStack align="start" spacing="2">
                        <Text fontWeight="bold">Final Pricing:</Text>
                        {discountPercent > 0 && (
                          <>
                            <Text>Base Price: {formatCurrency(pricing.basePrice)}</Text>
                            <Text color="green.600">
                              Discount ({discountPercent}%): -{formatCurrency(pricing.discountAmount || 0)}
                            </Text>
                          </>
                        )}
                        <Text fontSize="xl" fontWeight="bold" color="blue.600">
                          Total: {formatCurrency(pricing.finalPrice)}/year
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          Monthly: {formatCurrency(pricing.finalPrice / 12)}
                        </Text>
                      </VStack>
                    </Box>
                  )}
                </Stack>
              </CardBody>
            </Card>

            {/* ROI Calculator */}
            <Card>
              <CardHeader>
                <HStack>
                  <MdCalculate />
                  <Heading size="md">ROI Calculator</Heading>
                </HStack>
              </CardHeader>
              <CardBody>
                <Stack spacing="4">
                  <FormControl>
                    <FormLabel>Current Monthly Cost (existing solution)</FormLabel>
                    <NumberInput
                      value={currentMonthlyCost}
                      onChange={(_, val) => setCurrentMonthlyCost(val)}
                      min={0}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Time Currently Spent (hours/month)</FormLabel>
                    <NumberInput
                      value={timeCurrentlySpent}
                      onChange={(_, val) => setTimeCurrentlySpent(val)}
                      min={0}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Time With DossiLoc (hours/month)</FormLabel>
                    <NumberInput
                      value={timeWithDossiLoc}
                      onChange={(_, val) => setTimeWithDossiLoc(val)}
                      min={0}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Client's Hourly Rate</FormLabel>
                    <NumberInput
                      value={hourlyRate}
                      onChange={(_, val) => setHourlyRate(val)}
                      min={0}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </Stack>
              </CardBody>
            </Card>

            {/* Features Selection */}
            <Card>
              <CardHeader>
                <Heading size="md">Features & Benefits</Heading>
              </CardHeader>
              <CardBody>
                <Stack spacing="2">
                  {features.map((feature, index) => (
                    <HStack key={index} justify="space-between" p="2" bg={feature.highlighted ? 'blue.50' : 'transparent'} borderRadius="md">
                      <Checkbox
                        isChecked={feature.included}
                        onChange={() => handleFeatureToggle(index)}
                      >
                        <VStack align="start" spacing="0">
                          <Text fontWeight={feature.highlighted ? 'bold' : 'normal'}>
                            {feature.name}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            {feature.description}
                          </Text>
                        </VStack>
                      </Checkbox>
                    </HStack>
                  ))}
                </Stack>
              </CardBody>
            </Card>

            {/* Settings */}
            <Card>
              <CardHeader>
                <Heading size="md">Proposal Settings</Heading>
              </CardHeader>
              <CardBody>
                <Stack spacing="4">
                  <FormControl>
                    <FormLabel>Valid For (days)</FormLabel>
                    <NumberInput
                      value={validityDays}
                      onChange={(_, val) => setValidityDays(val)}
                      min={7}
                      max={90}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>

                  <Checkbox
                    isChecked={signatureRequired}
                    onChange={(e) => setSignatureRequired(e.target.checked)}
                  >
                    Require digital signature
                  </Checkbox>
                </Stack>
              </CardBody>
            </Card>
          </Stack>
        </GridItem>

        {/* Right Column - ROI Preview */}
        <GridItem colSpan={{ base: 12, lg: 5 }}>
          <Card position="sticky" top="100px">
            <CardHeader>
              <HStack>
                <MdPreview />
                <Heading size="md">ROI Summary</Heading>
              </HStack>
            </CardHeader>
            <CardBody>
              {roiCalculation ? (
                <Stack spacing="4">
                  <SimpleGrid columns={2} gap="4">
                    <Stat>
                      <StatLabel>Monthly Savings</StatLabel>
                      <StatNumber fontSize="lg">
                        {formatCurrency(roiCalculation.monthlySavings)}
                      </StatNumber>
                      <StatHelpText>
                        <StatArrow type={roiCalculation.monthlySavings > 0 ? 'increase' : 'decrease'} />
                        vs current cost
                      </StatHelpText>
                    </Stat>

                    <Stat>
                      <StatLabel>Annual Savings</StatLabel>
                      <StatNumber fontSize="lg">
                        {formatCurrency(roiCalculation.annualSavings)}
                      </StatNumber>
                    </Stat>

                    <Stat>
                      <StatLabel>Time Saved</StatLabel>
                      <StatNumber fontSize="lg">{roiCalculation.timeSaved}h</StatNumber>
                      <StatHelpText>per month</StatHelpText>
                    </Stat>

                    <Stat>
                      <StatLabel>Time Value</StatLabel>
                      <StatNumber fontSize="lg">
                        {formatCurrency(roiCalculation.timeSavingsValue)}
                      </StatNumber>
                      <StatHelpText>per month</StatHelpText>
                    </Stat>
                  </SimpleGrid>

                  <Divider />

                  <Box p="4" bg="green.50" borderRadius="md">
                    <VStack align="start" spacing="2">
                      <Text fontWeight="bold" fontSize="lg">Total Monthly Value</Text>
                      <Text fontSize="2xl" fontWeight="bold" color="green.600">
                        {formatCurrency(roiCalculation.totalMonthlyValue)}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {formatCurrency(roiCalculation.totalAnnualValue)}/year
                      </Text>
                    </VStack>
                  </Box>

                  <SimpleGrid columns={2} gap="4">
                    <Stat>
                      <StatLabel>ROI</StatLabel>
                      <StatNumber fontSize="lg" color="green.600">
                        {roiCalculation.roiPercentage.toFixed(0)}%
                      </StatNumber>
                    </Stat>

                    <Stat>
                      <StatLabel>Payback Period</StatLabel>
                      <StatNumber fontSize="lg">
                        {roiCalculation.paybackPeriod} mo
                      </StatNumber>
                    </Stat>
                  </SimpleGrid>

                  {roiCalculation.roiPercentage > 200 && (
                    <Alert status="success" borderRadius="md">
                      <AlertIcon />
                      <Text fontSize="sm">Exceptional ROI! Highlight this in the proposal.</Text>
                    </Alert>
                  )}
                </Stack>
              ) : (
                <Text color="gray.500">Complete the pricing and ROI fields to see the summary</Text>
              )}

              <Divider my="4" />

              <Stack spacing="3">
                <Button
                  leftIcon={<MdSave />}
                  onClick={handleCreateDraft}
                  isLoading={submitting}
                  w="full"
                >
                  Save as Draft
                </Button>
                <Button
                  leftIcon={<MdSend />}
                  colorScheme="brand"
                  onClick={handleCreateAndSend}
                  isLoading={submitting}
                  isDisabled={needsApproval && pricingApprovalStatus !== 'approved' && pricingApprovalStatus !== 'auto-approved'}
                  w="full"
                >
                  Create & Send Proposal
                </Button>
                
                {/* Pricing Approval Alerts */}
                {needsApproval && !pricingApprovalStatus && (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <VStack align="start" spacing="1">
                      <Text fontSize="sm" fontWeight="bold">
                        Custom Pricing Requires Approval
                      </Text>
                      <Text fontSize="xs">
                        This proposal has a discount ≥5% and requires manager approval. Save as draft first, then request approval.
                      </Text>
                    </VStack>
                  </Alert>
                )}
                
                {pricingApprovalStatus === 'pending' && (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Text fontSize="sm">
                      Awaiting pricing approval from management.
                    </Text>
                  </Alert>
                )}
                
                {pricingApprovalStatus === 'approved' && (
                  <Alert status="success" borderRadius="md">
                    <AlertIcon />
                    <Text fontSize="sm">
                      Pricing approved! You can now send the proposal.
                    </Text>
                  </Alert>
                )}
                <Button
                  variant="ghost"
                  onClick={() => navigate(`/admin/sales/leads/${leadId}`)}
                  w="full"
                >
                  Cancel
                </Button>
              </Stack>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>
    </Box>
  );
}
